import * as tslib_1 from "tslib";
import { environment } from '../../../environments/environment';
/**
 * Minimum distance when performing a radius search (nautical miles)
 */
export var MIN_RADIUS = 25;
/**
 * Maximum distance when performing a radius search (nautical miles)
 */
export var MAX_RADIUS = 500;
/**
 * Base HTTP service for the location-based business finders (Flying Clubs today; Flight Schools and
 * Universities later). Mirrors {@link AviationEventsService}: it POSTs to a single endpoint with one of
 * four request-body shapes (point+radius / rectangleLatLong / stateProvince / searchTerm). Subclasses
 * supply the endpoint path (via {@link endpoint}) and a {@link parse} function, and expose
 * domain-named wrappers around the protected `getBy*` methods.
 */
var BusinessFinderService = /** @class */ (function () {
    function BusinessFinderService(_http, _errorHandler) {
        this._http = _http;
        this._errorHandler = _errorHandler;
    }
    /**
     * Returns a sanitized search term with all invalid characters removed.
     */
    BusinessFinderService.sanitizeSearchTerm = function (term) {
        return (term || '').toString().replace(/[^a-z0-9 ]+/gi, '').trim();
    };
    Object.defineProperty(BusinessFinderService.prototype, "url", {
        get: function () {
            return environment.airportsApiUrl + "/" + this.endpoint;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Performs a radial search within a given distance of a center point.
     * @param latlon {LatLon} Center point of search
     * @param radius {number} Radius in nautical miles
     */
    BusinessFinderService.prototype.getByLatLon = function (latlon, radius) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_1;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!latlon) {
                            throw new Error('latlon is required');
                        }
                        if (!radius || radius < MIN_RADIUS || radius > MAX_RADIUS) {
                            throw new Error("radius is required and must be between " + MIN_RADIUS + " and " + MAX_RADIUS + " nautical miles.");
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(this.url, {
                                point: {
                                    latitude: latlon.lat,
                                    longitude: latlon.lon
                                },
                                radius: radius
                            }).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(function (d) { return _this.parse(d); })];
                    case 3:
                        e_1 = _a.sent();
                        this._errorHandler.handleError(e_1);
                        throw e_1;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Performs a bounding box (map area) search.
     * @param bounds {BoundingBox} Bounding box
     */
    BusinessFinderService.prototype.getByBoundingBox = function (bounds) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var neLat, neLon, swLat, swLon, response, e_2;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 2, , 3]);
                        neLat = bounds.ne.lat.toFixed(7);
                        neLon = bounds.ne.lon.toFixed(7);
                        swLat = bounds.sw.lat.toFixed(7);
                        swLon = bounds.sw.lon.toFixed(7);
                        return [4 /*yield*/, this._http.post(this.url, {
                                rectangleLatLong: neLat + "," + neLon + "," + swLat + "," + swLon
                            }).toPromise()];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response.map(function (d) { return _this.parse(d); })];
                    case 2:
                        e_2 = _a.sent();
                        this._errorHandler.handleError(e_2);
                        throw e_2;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns all results within a given state.
     * @param state {string} State code
     */
    BusinessFinderService.prototype.getByState = function (state) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_3;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!state) {
                            throw new Error('"state" parameter is required when searching by state');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(this.url, {
                                stateProvince: state
                            }).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(function (d) { return _this.parse(d); })];
                    case 3:
                        e_3 = _a.sent();
                        this._errorHandler.handleError(e_3);
                        throw e_3;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Returns results matching a keyword (name or airport ident).
     * @param term {string} Search term
     */
    BusinessFinderService.prototype.getBySearchTerm = function (term) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var response, e_4;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        term = BusinessFinderService.sanitizeSearchTerm(term);
                        if (term.length < 3) {
                            throw new Error('At least three alphanumeric characters are necessary to perform a search.');
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._http.post(this.url, {
                                searchTerm: "%" + term + "%"
                            }).toPromise()];
                    case 2:
                        response = _a.sent();
                        return [2 /*return*/, response.map(function (d) { return _this.parse(d); })];
                    case 3:
                        e_4 = _a.sent();
                        this._errorHandler.handleError(e_4);
                        throw e_4;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    return BusinessFinderService;
}());
export { BusinessFinderService };
